import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeft,
  Ban,
  Building2,
  Copy,
  FilePlus2,
  FileText,
  Home,
  Mail,
  MapPin,
  Phone,
  ShieldPlus,
  UserPlus,
} from 'lucide-react'
import { toast } from 'sonner'

import { AppShell } from '@/admin/components/app-shell'
import { CardActionMenu } from '@/admin/components/shared/card-action-menu'
import { AttachmentsCard } from '@/admin/components/shared/attachments-card'
import { EditFormDialog } from '@/admin/components/shared/edit-form-dialog'
import { DataTable, type DataTableColumn } from '@/admin/components/data-table'
import { usePermissions } from '@/admin/hooks/use-permissions'
import {
  useCopyRegistration,
  useCreateGroupClaim,
  useGroupProperties,
} from '@/admin/hooks/use-property-groups'
import {
  useAddCoOwner,
  useDeactivateRegistration,
  useProperty,
  type PropertyRecord,
} from '@/admin/hooks/use-properties'
import { toContactInput, useContactFields } from '@/admin/lib/property-group-forms'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs'
import { EntityHistoryCard } from '@/shared/components/workflows/entity-history-card'
import { toastApiError } from '@/shared/lib/api-error'
import { defineField, formatPhoneDisplay } from '@/shared/lib/forms'
import type { FormFieldMeta } from '@/shared/lib/forms'
import { useFormatters } from '@/shared/lib/formatting'

type Registration = PropertyRecord['registrations'][number]
type ClaimRow = PropertyRecord['claims'][number]
type TransferRow = PropertyRecord['transfers'][number]

/** Open first, then newest: open work is what the user acts on. */
function openFirst<T extends { is_open: boolean }>(rows: T[]): T[] {
  return [...rows].sort((a, b) => Number(b.is_open) - Number(a.is_open))
}

/**
 * A property: its registered products (with structure and warranty class),
 * structures, claims and transfers (open first, then history), documents and
 * change log. From here a user starts a registration or a claim, adds a
 * second owner, or deactivates a registration.
 */
export function PropertyPage() {
  const { uuid = '' } = useParams<{ uuid: string }>()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { can } = usePermissions()
  const { formatDate } = useFormatters()
  const { data: property, isLoading } = useProperty(uuid)

  const addCoOwner = useAddCoOwner(uuid)
  const deactivate = useDeactivateRegistration(uuid)
  const contactFields = useContactFields()
  const [coOwnerOpen, setCoOwnerOpen] = useState(false)
  const [claimOpen, setClaimOpen] = useState(false)
  // A property in an HOA: first ask whose claim it is (the HOA's, paid to the
  // HOA in one payment, or the owner's own), then open the matching form.
  const [claimChoiceOpen, setClaimChoiceOpen] = useState(false)
  const [groupClaimOpen, setGroupClaimOpen] = useState(false)
  const [deactivating, setDeactivating] = useState<Registration | null>(null)
  const [copying, setCopying] = useState<Registration | null>(null)
  const groupUuid = property?.hoa?.uuid ?? ''
  const { data: groupProperties = [] } = useGroupProperties(groupUuid || undefined)
  const copyRegistration = useCopyRegistration(groupUuid)
  const createGroupClaim = useCreateGroupClaim(groupUuid)

  const canEdit = can('holdings.edit.global')
  const canRegister = can('holdings.create')
  const canFileClaims = can('claims.create')

  const activeRegistrations = useMemo(
    () => (property?.registrations ?? []).filter((r) => r.is_active),
    [property],
  )

  const reasonFields = useMemo<FormFieldMeta[]>(
    () => [
      defineField({
        code_name: 'reason',
        display_name: t('property.deactivateReason', 'Reason'),
        field_type: 'TEXTAREA',
        required: true,
      }),
    ],
    [t],
  )
  const copyFields = useMemo<FormFieldMeta[]>(
    () => [
      defineField({
        code_name: 'property_uuids',
        display_name: t('property.copyTargets', 'Copy to these properties'),
        field_type: 'MULTI_SELECT',
        required: true,
        options: groupProperties
          .filter((p) => p.uuid !== uuid)
          .map((p) => ({ value: p.uuid, label: p.address })),
      }),
    ],
    [t, groupProperties, uuid],
  )
  const claimFields = useMemo<FormFieldMeta[]>(
    () => [
      defineField({
        code_name: 'holding',
        display_name: t('property.claimRegistration', 'Registration'),
        field_type: 'SELECT',
        required: true,
        options: activeRegistrations.map((r) => ({
          value: r.uuid,
          label: [r.registration_number, r.product, r.structure]
            .filter(Boolean)
            .join(' - '),
        })),
      }),
    ],
    [t, activeRegistrations],
  )
  const groupClaimFields = useMemo<FormFieldMeta[]>(
    () => [
      defineField({
        code_name: 'registrations',
        display_name: t('property.groupClaimRegistrations', 'Registrations on the claim'),
        field_type: 'MULTI_SELECT',
        required: true,
        options: activeRegistrations.map((r) => ({
          value: r.uuid,
          label: [r.registration_number, r.product, r.structure]
            .filter(Boolean)
            .join(' - '),
        })),
      }),
      defineField({
        code_name: 'description',
        display_name: t('groups.detail.newClaimDescription', 'What happened'),
        hint: t(
          'groups.detail.newClaimDescriptionHint',
          'Describe the damage and when it was first noticed',
        ),
        field_type: 'TEXTAREA',
        required: true,
      }),
    ],
    [t, activeRegistrations],
  )

  if (isLoading || !property) {
    return (
      <AppShell>
        <main className="page-container py-10">
          <p className="py-20 text-center text-[13px] text-[var(--ink-3)]">
            {isLoading
              ? t('common.loading', 'Loading...')
              : t('property.notFound', 'Property not found.')}
          </p>
        </main>
      </AppShell>
    )
  }

  const ownerName = `${property.owner.first_name} ${property.owner.last_name}`.trim()
  const mailing = property.mailing_address
  const mailingDisplay = mailing?.line1
    ? [mailing.line1, mailing.line2, mailing.city, mailing.state, mailing.zip_code]
        .filter(Boolean)
        .join(', ')
    : t('property.mailingSame', 'Same as the property address')

  /** Open the registration wizard already filled with this property. */
  const startRegistration = () => {
    const firstStructure = property.structures[0]?.values ?? {}
    navigate('/registrations/new', {
      state: {
        propertyRegistrationPrefill: {
          hostUuid: property.uuid,
          hostAttributeValues: {
            ...property.host_attribute_values,
            ...firstStructure,
          },
          hostColumnValues: {},
          structureAttributeValues: firstStructure,
          customerPrefill: {
            email: property.owner.email,
            firstName: property.owner.first_name,
            lastName: property.owner.last_name,
            phone: property.owner.phone,
          },
          earlyAttributesPrefill: {},
        },
      },
    })
  }

  const registrationColumns: DataTableColumn<Registration>[] = [
    {
      id: 'number',
      header: t('property.colRegistration', 'Registration'),
      cell: (r) => r.registration_number || '-',
    },
    {
      id: 'product',
      header: t('property.colProduct', 'Product'),
      cell: (r) => r.product || '-',
    },
    {
      id: 'structure',
      header: t('property.colStructure', 'Structure'),
      cell: (r) => r.structure || '-',
    },
    {
      id: 'class',
      header: t('property.colWarrantyClass', '{{warrantyNoun}} Class'),
      cell: (r) => r.warranty_class || '-',
    },
    {
      id: 'status',
      header: t('property.colStatus', 'Status'),
      cell: (r) => (
        <Badge variant={r.is_active ? 'secondary' : 'outline'}>{r.status_label}</Badge>
      ),
    },
    {
      id: 'date',
      header: t('property.colRegistered', 'Registered'),
      cell: (r) => (r.registered_at ? formatDate(r.registered_at) : '-'),
    },
    ...(canEdit
      ? [
          {
            id: 'deactivate',
            header: '',
            // Pinned right so the row's actions stay in view at any width.
            sticky: 'right' as const,
            cell: (r: Registration) =>
              r.is_active ? (
                // One quiet "more" button per registration, like the HOA's
                // property rows: these actions are occasional.
                <span onClick={(e) => e.stopPropagation()}>
                  <CardActionMenu
                    triggerLabel={t('property.rowActions', 'More actions')}
                    triggerClassName="h-7 w-7 border-0 bg-transparent text-[var(--ink-3)] hover:text-[var(--ink)]"
                    items={[
                      ...(property.group
                        ? [
                            {
                              key: 'copy',
                              label: t('property.copyToGroupProperties', 'Copy to {{groupNoun}} properties'),
                              icon: <Copy className="h-3.5 w-3.5" />,
                              onSelect: () => setCopying(r),
                            },
                          ]
                        : []),
                      {
                        key: 'deactivate',
                        label: t('property.deactivate', 'Deactivate'),
                        icon: <Ban className="h-3.5 w-3.5" />,
                        destructive: true,
                        onSelect: () => setDeactivating(r),
                      },
                    ]}
                  />
                </span>
              ) : null,
          },
        ]
      : []),
  ]

  const claimColumns: DataTableColumn<ClaimRow>[] = [
    {
      id: 'number',
      header: t('property.colClaim', 'Claim'),
      cell: (c) => c.claim_number || '-',
    },
    {
      id: 'registration',
      header: t('property.colRegistration', 'Registration'),
      cell: (c) => c.registration_number || '-',
    },
    {
      id: 'status',
      header: t('property.colStatus', 'Status'),
      cell: (c) => (
        <span className="flex items-center gap-2">
          <Badge variant={c.is_open ? 'secondary' : 'outline'}>
            {c.is_open ? t('property.open', 'Open') : t('property.closed', 'Closed')}
          </Badge>
          <span className="text-[12px] text-[var(--ink-3)]">{c.status_label}</span>
        </span>
      ),
    },
    {
      id: 'date',
      header: t('property.colClaimDate', 'Date of Claim'),
      cell: (c) => (c.created_at ? formatDate(c.created_at) : '-'),
    },
  ]

  const transferColumns: DataTableColumn<TransferRow>[] = [
    {
      id: 'reference',
      header: t('property.colTransfer', 'Transfer'),
      cell: (tr) => tr.reference,
    },
    {
      id: 'status',
      header: t('property.colStatus', 'Status'),
      cell: (tr) => (
        <Badge variant={tr.is_open ? 'secondary' : 'outline'}>{tr.status_label}</Badge>
      ),
    },
    {
      id: 'updated',
      header: t('property.colUpdated', 'Last Updated'),
      cell: (tr) => (tr.updated_at ? formatDate(tr.updated_at) : '-'),
    },
  ]

  const openClaims = property.claims.filter((c) => c.is_open).length
  const openTransfers = property.transfers.filter((tr) => tr.is_open).length

  return (
    <AppShell>
      <main className="page-container py-10">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          className="mb-4 cursor-pointer gap-1 px-0 text-[11px] text-[var(--ink-3)]"
        >
          <ArrowLeft className="h-3 w-3" />
          {t('common.back', 'Back')}
        </Button>

        <div className="mb-8 flex items-start gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border-[0.5px] border-[var(--green-border)] bg-[var(--green-bg)]">
            <Home className="h-6 w-6 text-[var(--green)]" />
          </div>
          <div className="min-w-0 flex-1">
            {/* Street as the heading, town below: a full address in title
                type wrapped to four lines. */}
            <h1 className="clara-title mb-1">
              {[property.address.line1, property.address.line2].filter(Boolean).join(', ') ||
                property.address_display}
            </h1>
            <p className="mb-2 text-[15px] text-[var(--ink-2)]">
              {[
                property.address.city,
                [property.address.state, property.address.zip_code].filter(Boolean).join(' '),
              ]
                .filter(Boolean)
                .join(', ')}
            </p>
            <div className="flex flex-wrap items-center gap-4 text-[12px] text-[var(--ink-3)]">
              {property.group && (
                <Button
                  variant="link"
                  size="xs"
                  onClick={() => navigate(`/groups/${property.group?.uuid}`)}
                  className="cursor-pointer gap-1 px-0"
                >
                  <Building2 className="h-3 w-3" />
                  {property.group.name}
                </Button>
              )}
              <span>{ownerName || '-'}</span>
              {property.owner.email && (
                <span className="flex items-center gap-1.5">
                  <Mail className="h-3 w-3" />
                  {property.owner.email}
                </span>
              )}
              {property.owner.phone && (
                <span className="flex items-center gap-1.5">
                  <Phone className="h-3 w-3" />
                  {formatPhoneDisplay(property.owner.phone)}
                </span>
              )}
            </div>
            <p className="mt-1 flex items-center gap-1.5 text-[12px] text-[var(--ink-3)]">
              <MapPin className="h-3 w-3" />
              {t('property.mailing', 'Mailing:')} {mailingDisplay}
            </p>
          </div>
          <div className="flex gap-2">
            {canRegister && (
              <Button
                variant="outline"
                onClick={startRegistration}
                className="h-9 cursor-pointer gap-1.5"
              >
                <ShieldPlus className="h-3.5 w-3.5" />
                {t('property.newRegistration', 'New Registration')}
              </Button>
            )}
            {canFileClaims && (
              <Button
                onClick={() => (property.group ? setClaimChoiceOpen(true) : setClaimOpen(true))}
                disabled={activeRegistrations.length === 0}
                className="h-9 cursor-pointer gap-1.5"
              >
                <FilePlus2 className="h-3.5 w-3.5" />
                {t('property.newClaim', 'New Claim')}
              </Button>
            )}
          </div>
        </div>

        <Tabs defaultValue="registrations">
          <TabsList variant="line">
            <TabsTrigger value="registrations">
              {t('property.tabRegistrations', 'Registered Products ({{count}})', {
                count: activeRegistrations.length,
              })}
            </TabsTrigger>
            <TabsTrigger value="structures">
              {t('property.tabStructures', 'Structures ({{count}})', {
                count: property.structures.length,
              })}
            </TabsTrigger>
            <TabsTrigger value="claims">
              {t('property.tabClaims', 'Claims ({{count}} open)', { count: openClaims })}
            </TabsTrigger>
            <TabsTrigger value="transfers">
              {t('property.tabTransfers', 'Transfers ({{count}} open)', {
                count: openTransfers,
              })}
            </TabsTrigger>
            <TabsTrigger value="owners">{t('property.tabOwners', 'Owners')}</TabsTrigger>
            <TabsTrigger value="documents">
              {t('property.tabDocuments', 'Documents')}
            </TabsTrigger>
            <TabsTrigger value="changes">
              {t('property.tabChangeLog', 'Change Log')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="registrations" className="mt-6">
            <DataTable<Registration>
              tableId="property-registrations"
              columns={registrationColumns}
              data={property.registrations}
              rowKey={(r) => r.uuid}
              onRowClick={(r) => navigate(`/registrations/${r.uuid}`)}
              emptyMessage={t('property.noRegistrations', 'No registrations yet.')}
            />
          </TabsContent>

          <TabsContent value="structures" className="mt-6">
            <div className="flex flex-col gap-2">
              {property.structures.length === 0 && (
                <p className="text-[12px] text-[var(--ink-3)]">
                  {t('property.noStructures', 'No structures recorded.')}
                </p>
              )}
              {property.structures.map((s) => (
                <div
                  key={s.uuid}
                  className="rounded-lg border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] px-4 py-3"
                >
                  <p className="text-[13px] text-[var(--ink)]">{s.label}</p>
                  <p className="mt-1 text-[12px] text-[var(--ink-3)]">
                    {Object.values(s.values)
                      .filter((v) => v !== null && v !== '' && typeof v !== 'object')
                      .map(String)
                      .join(' - ')}
                  </p>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="claims" className="mt-6">
            <DataTable<ClaimRow>
              tableId="property-claims"
              columns={claimColumns}
              data={openFirst(property.claims)}
              rowKey={(c) => c.uuid}
              onRowClick={(c) => navigate(`/claims/${c.uuid}`)}
              emptyMessage={t('property.noClaims', 'No claims on this property.')}
            />
          </TabsContent>

          <TabsContent value="transfers" className="mt-6">
            <DataTable<TransferRow>
              tableId="property-transfers"
              columns={transferColumns}
              data={openFirst(property.transfers)}
              rowKey={(tr) => tr.uuid}
              onRowClick={(tr) => navigate(`/transfers/${tr.uuid}`)}
              emptyMessage={t('property.noTransfers', 'No transfers on this property.')}
            />
          </TabsContent>

          <TabsContent value="owners" className="mt-6">
            <div className="flex flex-col gap-2">
              <div className="rounded-lg border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] px-4 py-3">
                <p className="text-[13px] text-[var(--ink)]">
                  {ownerName || '-'}
                  <Badge variant="secondary" className="ml-2">
                    {t('property.owner', 'Owner')}
                  </Badge>
                </p>
                <p className="mt-1 text-[12px] text-[var(--ink-3)]">
                  {[property.owner.email, property.owner.phone].filter(Boolean).join(' - ')}
                </p>
              </div>
              {property.co_owners.map((c) => (
                <div
                  key={c.uuid}
                  className="rounded-lg border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] px-4 py-3"
                >
                  <p className="text-[13px] text-[var(--ink)]">
                    {`${c.first_name} ${c.last_name}`.trim()}
                    <Badge variant="outline" className="ml-2">
                      {t('property.secondOwner', 'Second Owner')}
                    </Badge>
                  </p>
                  <p className="mt-1 text-[12px] text-[var(--ink-3)]">
                    {[c.email, c.phone].filter(Boolean).join(' - ')}
                  </p>
                </div>
              ))}
              {canEdit && property.co_owners.length === 0 && (
                <div>
                  <Button
                    variant="outline"
                    onClick={() => setCoOwnerOpen(true)}
                    className="h-9 cursor-pointer gap-1.5"
                  >
                    <UserPlus className="h-3.5 w-3.5" />
                    {t('property.addSecondOwner', 'Add Second Owner')}
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="documents" className="mt-6">
            <AttachmentsCard
              subjectType="host"
              subjectUuid={property.uuid}
              useCase="WARRANTY_DOCUMENT"
              title={t('property.documents', 'Documents')}
              icon={<FileText className="h-3.5 w-3.5" />}
              canAdd={canEdit}
              canDelete={canEdit}
            />
          </TabsContent>

          <TabsContent value="changes" className="mt-6">
            <EntityHistoryCard
              entityKeys={['host', ...property.registrations.map(() => 'holding')]}
              entityIds={[property.uuid, ...property.registrations.map((r) => r.uuid)]}
              title={t('property.changeLog', 'Change Log')}
            />
          </TabsContent>
        </Tabs>

        <EditFormDialog
          open={coOwnerOpen}
          onOpenChange={setCoOwnerOpen}
          title={t('property.addSecondOwner', 'Add Second Owner')}
          fields={contactFields}
          values={{ first_name: '', last_name: '', email: '', phone: '' }}
          isPending={addCoOwner.isPending}
          onSave={async (values) => {
            try {
              await addCoOwner.mutateAsync(toContactInput(values))
              toast.success(t('property.secondOwnerAdded', 'Second owner added'))
              setCoOwnerOpen(false)
            } catch (err) {
              toastApiError(err, t('property.secondOwnerFailed', 'Could not add the second owner'))
              return false
            }
          }}
        />

        <EditFormDialog
          open={deactivating !== null}
          onOpenChange={(o) => !o && setDeactivating(null)}
          title={t('property.deactivateTitle', 'Deactivate {{number}}?', {
            number: deactivating?.registration_number ?? '',
          })}
          fields={reasonFields}
          values={{ reason: '' }}
          isPending={deactivate.isPending}
          onSave={async (values) => {
            if (!deactivating) return
            try {
              await deactivate.mutateAsync({
                holdingUuid: deactivating.uuid,
                reason: String(values.reason ?? ''),
              })
              toast.success(t('property.deactivated', 'Registration deactivated'))
              setDeactivating(null)
            } catch (err) {
              toastApiError(err, t('property.deactivateFailed', 'Could not deactivate'))
              return false
            }
          }}
        />

        <EditFormDialog
          open={copying !== null}
          onOpenChange={(o) => !o && setCopying(null)}
          title={t('property.copyTitle', 'Copy {{number}} to other {{group}} properties', {
            number: copying?.registration_number ?? '',
            group: property.group?.name ?? '',
          })}
          fields={copyFields}
          values={{ property_uuids: [] }}
          isPending={copyRegistration.isPending}
          onSave={async (values) => {
            if (!copying) return
            try {
              const res = await copyRegistration.mutateAsync({
                registration_uuid: copying.uuid,
                property_uuids: (values.property_uuids as string[]) ?? [],
              })
              toast.success(
                t(
                  'property.copyDone',
                  'Registered on {{registered}} properties; {{already}} already had it.',
                  { registered: res.registered, already: res.already_registered },
                ),
              )
              for (const r of res.results.filter((x) => x.error)) {
                toast.error(`${r.address ?? r.property_uuid}: ${r.error}`)
              }
              setCopying(null)
            } catch (err) {
              toastApiError(err, t('property.copyFailed', 'Could not copy the registration'))
              return false
            }
          }}
        />

        <Dialog open={claimChoiceOpen} onOpenChange={setClaimChoiceOpen}>
          <DialogContent className="sm:max-w-[460px]">
            <DialogHeader>
              <DialogTitle className="text-[15px] font-normal text-[var(--ink)]">
                {t('property.claimChoiceTitle', 'Is this claim for the {{groupNoun}} or the owner?')}
              </DialogTitle>
              <DialogDescription className="text-[12px] leading-[1.5] text-[var(--ink-3)]">
                {t(
                  'property.claimChoiceBody',
                  'This property is part of {{group}}. A {{groupNoun}} claim is handled with the {{groupNoun}} and paid to the {{groupNoun}} in one payment. The owner\'s own claim is handled and paid like any single claim.',
                  { group: property.group?.name ?? '' },
                )}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                className="cursor-pointer"
                onClick={() => {
                  setClaimChoiceOpen(false)
                  setClaimOpen(true)
                }}
              >
                {t('property.claimChoiceOwner', "Owner's own claim")}
              </Button>
              <Button
                className="cursor-pointer"
                onClick={() => {
                  setClaimChoiceOpen(false)
                  setGroupClaimOpen(true)
                }}
              >
                {t('property.claimChoiceGroup', '{{groupNoun}} claim')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <EditFormDialog
          open={groupClaimOpen}
          onOpenChange={setGroupClaimOpen}
          title={t('groups.detail.newClaimTitle', 'New claim for {{name}}', {
            name: property.group?.name ?? '',
          })}
          fields={groupClaimFields}
          values={{
            registrations: activeRegistrations.length === 1 ? [activeRegistrations[0].uuid] : [],
            description: '',
          }}
          isPending={createGroupClaim.isPending}
          submitLabel={t('groups.detail.newClaimSubmit', 'Create Claim')}
          onSave={async (values) => {
            try {
              const claim = await createGroupClaim.mutateAsync({
                registrations: (values.registrations as string[]) ?? [],
                description: String(values.description ?? ''),
              })
              toast.success(
                t('groups.detail.newClaimCreated', 'Claim {{number}} opened', {
                  number: claim.claim_number,
                }),
              )
              setGroupClaimOpen(false)
              navigate(`/claims/${claim.uuid}`)
            } catch (err) {
              toastApiError(err, t('groups.detail.newClaimFailed', 'Could not open the claim'))
              return false
            }
          }}
        />

        <EditFormDialog
          open={claimOpen}
          onOpenChange={setClaimOpen}
          title={t('property.newClaimTitle', 'Which registration is the claim for?')}
          fields={claimFields}
          values={{ holding: activeRegistrations[0]?.uuid ?? '' }}
          submitLabel={t('property.startClaim', 'Start Claim')}
          onSave={async (values) => {
            setClaimOpen(false)
            navigate(`/claims/submit?holding=${String(values.holding ?? '')}`)
          }}
        />
      </main>
    </AppShell>
  )
}
